/* eslint-disable no-else-return */
import { chain, isEmpty } from "lodash";
import { Flex, Icon, Small, Badge, Divider, Popover } from "orcs-design-system";
import PropTypes from "prop-types";
import React, { useCallback, useMemo, useState } from "react";
import { useLocation } from "react-router";
import pluralize from "pluralize";
import useRestricted from "src/app/Restricted/useRestricted";
import FteMemberBadge from "src/components/FteBadge";
import ReportTeamChange from "src/components/Person/ReportTeamChange";
import { useAuth0 } from "src/contexts/auth0Provider";
import { useFeatureFlags } from "src/contexts/global/FeatureFlagsContext";
import { useAllocationProject } from "src/contexts/global/GlobalContext";
import { useWorkspace } from "src/contexts/global/WorkspaceContext";
import { useModal } from "src/contexts/withModal";
import PersonPropType from "src/custom-prop-types/person";
import { getAllBadgeGroups } from "src/util/group";
import PersonAllocationsModal from "src/components/Person/AllocationsModal";
import { ENTITY_TYPES } from "src/consts/entityTypes";
import TagDisplay from "src/components/TagDisplay";
import { ACTION_TYPES } from "src/components/OrgChart/context/actionTypes";
import NodeDetailsIcon from "src/components/NodeVisualizer/components/NodeDetailsIcon";
import { EventBus } from "src/services/eventEmitter";
import {
  PANEL_ID,
  ORG_CHART_PANEL_EVENTS,
} from "src/components/OrgChart/components/OrgChartPanel/const";
import { getLocalStorageItem } from "src/services/localStorage";
import { ORG_CHART_DEFAULT_PANEL_ID } from "src/components/OrgChart/utils/storeInLocalStorage";
import { isReadOnly } from "src/components/OrgChart/context/utils/getCalculatedState";
import { getDirectReportsCount } from "src/util/person";
import PersonAvatarAndDetails from "../../../Person/PersonItem/sub-components/PersonAvatarAndDetails";
import { isRealTimeAllocationsEnabled } from "../../../../util/allocations";
import { isSuggestChangeEnabled } from "../../../../util/featureFlags";
import GroupBadge from "../../../Badge/GroupBadge";
import { useOrgChartContext } from "../../../OrgChart/context/OrgChartContext";
import {
  SourceHandle,
  StyledCard,
  StyledSimpleButton,
  TargetHandle,
  DIFF_STATE_THEMES,
  changes,
} from "../node.styled";
import { useNodeProperties } from "../../hooks/useNodeProperties";
import { applySelectedTheme } from "../utils/applySelectedTheme";
import { useNodeSelection } from "../utils/useNodeSelection";
import { usePersonNode } from "./PersonNodeProvider";
import { getAllocatedFteForOrgChart } from "./getAllocatedFteForOrgChart";
import { getNameWithFte } from "./getNameWithFte";
import PersonNodeSkeleton from "./PersonNodeSkeleton";
import PersonActionsMenu from "./PersonActionsMenu";

export const PERSON_CARD_THEMES = {
  default: {
    color: "greyLight",
    light: "greyLight",
    alt: "greyLight",
  },
  currentPerson: {
    color: "primaryLight",
    light: "primaryLight",
    alt: "primaryLight",
  },
  diffStates: DIFF_STATE_THEMES,
};

const getTheme = (isCurrentPerson, diffState, isSelected = false) => {
  // Current person always uses currentPerson theme (not affected by selected state)
  if (isCurrentPerson) {
    return PERSON_CARD_THEMES.currentPerson;
  }

  const baseTheme = diffState
    ? PERSON_CARD_THEMES?.diffStates?.[diffState]
    : PERSON_CARD_THEMES.default;

  return applySelectedTheme(baseTheme, isSelected);
};

const PersonNode = ({
  data,
  overrideHidden = false,
  className,
  collectionGroupId,
  pinnable = false,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const person = data;
  const { jobTitle } = data;
  const workspace = useWorkspace();
  const { tagConfig = {}, groupTypes } = workspace?.config || {};
  const { onExpandManagingClick } = usePersonNode();
  const orgChartContext = useOrgChartContext();
  const {
    reloadOrgChart,
    state,
    dispatch: orgChartDispatch,
  } = orgChartContext || {};

  // Check if we're in org chart context (team details > org chart)
  const isInOrgChartContext = !!orgChartContext;

  const { currentViewTeam, viewOptions = {} } = state || {};
  const { groupMembersBySupplyOrDemand } = viewOptions;

  const groupBadgeIdsToHide = useMemo(() => {
    if (!currentViewTeam) {
      return [];
    }
    const ids = [currentViewTeam.id];

    if (groupMembersBySupplyOrDemand) {
      ids.push(collectionGroupId);
    }

    return ids;
  }, [currentViewTeam, collectionGroupId, groupMembersBySupplyOrDemand]);

  const location = useLocation();
  const allocationProject = useAllocationProject();
  const { user } = useAuth0();
  const featureFlags = useFeatureFlags();
  const [, dispatch] = useModal();
  const [isDirectoryUser] = useRestricted();

  const [isCurrentPerson, isCurrentPersonManager] = useMemo(() => {
    return [
      location.pathname.includes(data.aggregateId),
      data.managing?.some((managee) =>
        location.pathname.includes(managee.aggregateId)
      ) ?? false,
    ];
  }, [data.aggregateId, data.managing, location.pathname]);

  const badgeGroups = useMemo(() => {
    return getAllBadgeGroups({ person, groupTypes });
  }, [person, groupTypes]);

  const onDisplayReportTeamChange = useCallback(() => {
    const onToggleVisibility = (isModalVisible) => () => {
      dispatch({ type: "setModalVisible", isModalVisible });
    };

    const modalComponent = (
      <ReportTeamChange
        person={person}
        groupTypes={groupTypes}
        isVisible={true}
        onToggleVisibility={onToggleVisibility}
        workspace={workspace}
        featureFlags={featureFlags}
      />
    );

    dispatch({ type: "setModalComponent", modalComponent });
  }, [dispatch, featureFlags, groupTypes, person, workspace]);

  const onEditingAllocations = useCallback(() => {
    const onHideModal = () => {
      dispatch({ type: "setModalVisible", isModalVisible: false });
      reloadOrgChart?.();
    };

    const personForUpdate = { ...person };
    const modalComponent = (
      <PersonAllocationsModal
        isVisible={true}
        person={personForUpdate}
        workspace={workspace}
        groupTypes={groupTypes}
        allocationProject={allocationProject}
        onHideModal={onHideModal}
        currentGroup={currentViewTeam}
        forceUpdatePerson
      />
    );
    dispatch({ type: "setModalComponent", modalComponent });
  }, [
    allocationProject,
    dispatch,
    groupTypes,
    person,
    workspace,
    reloadOrgChart,
    currentViewTeam,
  ]);

  const ManagingButton = useMemo(() => {
    const directReportsCount = getDirectReportsCount(data);
    if (directReportsCount === 0) {
      return null;
    }

    const reportNum = directReportsCount.toLocaleString();
    const reportTxt = pluralize("direct report", directReportsCount);
    const instruction = !isCurrentPerson
      ? `Click to ${data.expanded ? "hide" : "view"}.`
      : "";

    const directReportsTooltipText = `${reportNum} ${reportTxt}. ${instruction}`;

    const chevronIcon =
      !isCurrentPerson && isCurrentPersonManager
        ? data.expanded
          ? "chevron-up"
          : "chevron-down"
        : !isCurrentPerson && !isInOrgChartContext
        ? "chevron-right"
        : null;

    return (
      <Popover
        text={directReportsTooltipText}
        direction="top"
        width="fit-content"
      >
        <StyledSimpleButton
          onClick={() => onExpandManagingClick?.(data, isCurrentPersonManager)}
          disabled={isCurrentPerson}
        >
          {reportNum}
          <Icon icon="user-friends" size="sm" color="greyDarker" />
          {chevronIcon && (
            <Icon size="sm" icon={chevronIcon} color="greyDarker" />
          )}
        </StyledSimpleButton>
      </Popover>
    );
  }, [
    data,
    isCurrentPerson,
    isCurrentPersonManager,
    isInOrgChartContext,
    onExpandManagingClick,
  ]);

  const tagsToBeDisplayed = useNodeProperties({
    entityType: ENTITY_TYPES.PERSON,
    tags: person.tags,
  });

  const groupBadges = useMemo(() => {
    if (isEmpty(badgeGroups)) {
      return null;
    }

    return chain(badgeGroups)
      .map((badgeGroup) => {
        const result = getAllocatedFteForOrgChart({
          group: badgeGroup,
          person,
          groupTypes,
        });

        return {
          ...result,
          badgeGroup,
        };
      })
      .filter(({ badgeGroup, isSupply }) => {
        // only hide group badges that are repeated on the card if
        // we are on a supply team.
        //
        // demand teams - we always show the badges, since people can
        // be part of multiple demand teams, and so it gets confusing
        // if you do not show all the badges.
        if (isSupply) {
          return !groupBadgeIdsToHide.includes(badgeGroup.id);
        } else {
          return true;
        }
      })
      .orderBy(
        [
          ({ isSupply }) => !isSupply, // false sorts before true, putting supply first
          "fte",
        ],
        ["asc", "desc"]
      )
      .map(({ badgeGroup, fte }) => {
        const badgeGroupWithFteInName = {
          ...badgeGroup,
          name: getNameWithFte(badgeGroup.name, fte),
        };

        return (
          <GroupBadge
            aria-label="Team and Hierarchy"
            group={badgeGroupWithFteInName}
            groupTypes={groupTypes}
            key={badgeGroup.id}
          />
        );
      })
      .value();
  }, [badgeGroups, groupTypes, person, groupBadgeIdsToHide]);

  const $hidden = !overrideHidden && (data.hidden || !data.isLayouted);
  const canChangeAllocations = isRealTimeAllocationsEnabled({
    user,
    workspace,
    allocationProject,
  });
  const canReportTeamChange = isSuggestChangeEnabled(workspace);

  const isSelected = useNodeSelection(state, data.aggregateId);

  const handlePersonClick = useCallback(() => {
    // Orcs FloatingPanels listens to this event - preserve the user's current tab
    // AI Summary is not available for people, so fall back to details
    const currentPanelId =
      getLocalStorageItem(ORG_CHART_DEFAULT_PANEL_ID) || PANEL_ID.details;
    const panelId =
      currentPanelId === PANEL_ID.ai ? PANEL_ID.details : currentPanelId;
    EventBus.emit(ORG_CHART_PANEL_EVENTS.SET_SELECTED_PANEL, panelId);

    const isEntitySelected =
      state?.expandedPanel?.data?.selectedEntity === data.aggregateId;
    // not selected?
    if (orgChartDispatch && !isEntitySelected) {
      // select
      orgChartDispatch({
        type: ACTION_TYPES.UPDATE_EXPANDED_PANEL,
        payload: {
          data: {
            selectedEntity: data.aggregateId,
            selectedEntityType: ENTITY_TYPES.PERSON,
          },
        },
      });
    }
  }, [
    data.aggregateId,
    orgChartDispatch,
    state?.expandedPanel?.data?.selectedEntity,
  ]);

  const readOnly = useMemo(() => isReadOnly(data), [data]);

  if (data.isLoading) {
    return <PersonNodeSkeleton />;
  }

  return (
    <StyledCard
      onClick={handlePersonClick}
      className={className}
      $hidden={$hidden}
      width="auto"
      minWidth="350px"
      maxWidth="350px"
      cursor="grab"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      $theme={getTheme(
        isCurrentPerson,
        data?.diff?.[collectionGroupId]?._type,
        isSelected
      )}
      active={isSelected}
    >
      <TargetHandle $hidden type="target" position="top" />

      <Flex justifyContent="space-between">
        <PersonAvatarAndDetails
          debug
          tagConfig={tagConfig}
          showLocalTime
          person={data}
          badgeGroups={badgeGroups}
          cardView={true}
          groupTypes={groupTypes}
          canChangeAllocations={canChangeAllocations}
          canReportTeamChange={canReportTeamChange}
          onDisplayReportTeamChange={onDisplayReportTeamChange}
          onEditingAllocations={onEditingAllocations}
          workspace={workspace}
          user={user}
          allocationProject={allocationProject}
          subtitleContent={
            <Small color="greyDark" mr="xs" mt="xs">
              {jobTitle}
            </Small>
          }
        />

        {!isDirectoryUser && !readOnly && (
          <PersonActionsMenu
            person={person}
            canChangeAllocations={canChangeAllocations}
            onEditingAllocations={onEditingAllocations}
            canReportTeamChange={canReportTeamChange}
            onDisplayReportTeamChange={onDisplayReportTeamChange}
          />
        )}
      </Flex>
      <Flex
        mt="s"
        flexDirection="row"
        flexWrap="nowrap"
        justifyContent="space-between"
      >
        <Flex
          flexDirection="row"
          flexWrap="nowrap"
          justifyContent="flex-start"
          alignItems="center"
        >
          {ManagingButton}
        </Flex>
        <Flex
          flexDirection="row"
          flexWrap="nowrap"
          justifyContent="flex-end"
          alignItems="center"
        >
          <NodeDetailsIcon
            enabled={pinnable || isInOrgChartContext}
            isSelected={isSelected}
            isHovered={isHovered}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handlePersonClick();
            }}
          />
        </Flex>
      </Flex>

      <Divider light mb="s" mt="s" />

      <Flex flexWrap="wrap" alignItems="center" role="list" gap="3px">
        <FteMemberBadge showToolTip={true} fte={person.fte} isCapacity={true} />
        {groupBadges}
        {data?.diff?.[collectionGroupId]?._type && (
          <Badge
            variant={
              DIFF_STATE_THEMES[data?.diff?.[collectionGroupId]?._type]?.color
            }
            altStyle
          >
            {changes[data?.diff?.[collectionGroupId]?._type]}
          </Badge>
        )}
      </Flex>

      {!isEmpty(tagsToBeDisplayed) && (
        <>
          <Divider light mb="s" mt="s" />

          <Flex
            justifyContent="space-between"
            flexWrap="nowrap"
            alignItems="flex-end"
          >
            <TagDisplay
              tags={tagsToBeDisplayed}
              entityType={ENTITY_TYPES.PERSON}
            />
          </Flex>
        </>
      )}

      <SourceHandle $hidden type="source" position="bottom" />
    </StyledCard>
  );
};

PersonNode.propTypes = {
  data: PersonPropType,
  overrideHidden: PropTypes.bool,
  className: PropTypes.string,
  collectionGroupId: PropTypes.string,
  pinnable: PropTypes.bool,
};

export default PersonNode;
